import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { searchSm } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo, useState } from "react";

import type { SelectDescriptor } from "~/features/commodities/hooks/use-commodity-selection.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";

import {
	useDeleteTemplate,
	useTemplateDefault,
} from "../../hooks/use-templates.ts";
import { DeleteTemplateModal } from "../delete-template-modal/delete-template-modal.tsx";
import { TemplateCard } from "../template-card/template-card.tsx";

type TemplatesModalMobileProps = {
	templates: TemplateGridItem[] | undefined;
	customerId: number | undefined;
	isLoading: boolean;
	select: SelectDescriptor;
	onSelect: (id: number | string) => void;
	onDeselect: () => void;
	onEdit: (templateId: number) => void;
};

export const TemplatesModalMobile = ({
	templates,
	customerId,
	isLoading,
	select,
	onSelect,
	onDeselect,
	onEdit,
}: TemplatesModalMobileProps) => {
	const [searchText, setSearchText] = useState("");
	const [templateToDelete, setTemplateToDelete] = useState<
		TemplateGridItem | undefined
	>(undefined);

	const filteredTemplates = useMemo(() => {
		const list = templates ?? [];
		const normalizedSearch = searchText.trim().toLowerCase();
		if (!normalizedSearch) return list;

		return list.filter((template) =>
			[
				template.templateName,
				template.shipperName,
				template.pickupStreet,
				template.pickupLocation,
				template.deliveryName,
				template.deliveryStreet,
				template.deliveryLocation,
				template.commodity,
				template.nmfc,
				template.freightClass,
				template.quantity,
				template.account,
			].some((value) => value.toLowerCase().includes(normalizedSearch)),
		);
	}, [templates, searchText]);

	const { toggleDefault, userConfigs, isHandlingDefault } =
		useTemplateDefault(customerId);
	const { deleteTemplate, isDeleting } = useDeleteTemplate();

	const isBusy = isLoading || isHandlingDefault || isDeleting;

	const isDefaultTemplate = useCallback(
		(templateId: number) =>
			userConfigs.some(
				(c) =>
					c.configType === ConfigurationType.QuoteTemplateDefault &&
					Number(c.customerId) === Number(customerId) &&
					c.configValue === String(templateId),
			),
		[userConfigs, customerId],
	);

	const handleToggleSelect = useCallback(
		(templateId: number) => {
			if (select[templateId]) {
				onDeselect();
			} else {
				onSelect(templateId);
			}
		},
		[select, onSelect, onDeselect],
	);

	const handleToggleDefault = useCallback(
		(templateId: number, shouldBeDefault: boolean) => {
			void toggleDefault(templateId, shouldBeDefault);
		},
		[toggleDefault],
	);

	const handleConfirmDelete = useCallback(() => {
		const target = templateToDelete;
		if (target) {
			Promise.resolve(deleteTemplate(target.templateId))
				.then(() => {
					// Clear a stale selection so Apply Template can't act on a deleted item.
					if (select[target.templateId]) {
						onDeselect();
					}
				})
				.catch(() => undefined);
		}
		setTemplateToDelete(undefined);
	}, [deleteTemplate, templateToDelete, select, onDeselect]);

	let content;
	if (isLoading) {
		content = (
			<div className={"templates-mobile-state"} role={"status"}>
				Loading templates...
			</div>
		);
	} else if (filteredTemplates.length > 0) {
		content = (
			<div aria-label={"Templates"} className={"templates-mobile-list"}>
				{filteredTemplates.map((template) => (
					<TemplateCard
						key={template.templateId}
						isBusy={isBusy}
						isDefault={isDefaultTemplate(template.templateId)}
						isSelected={!!select[template.templateId]}
						onDelete={setTemplateToDelete}
						onEdit={onEdit}
						onToggleDefault={handleToggleDefault}
						onToggleSelect={handleToggleSelect}
						selectable={true}
						template={template}
					/>
				))}
			</div>
		);
	} else {
		content = (
			<div className={"templates-mobile-state"} role={"status"}>
				{templates && templates.length > 0
					? "No matching templates found."
					: "No templates found."}
			</div>
		);
	}

	return (
		<div className={"templates-mobile"} data-testid={"templates-modal-mobile"}>
			<div className={"templates-mobile-search-row"}>
				<div className={"templates-mobile-search-input-wrapper"}>
					<SvgIcon className={"templates-mobile-search-icon"} icon={searchSm} />
					<input
						aria-label={"Search templates"}
						className={"templates-mobile-search"}
						data-testid={"templates-modal-mobile-search"}
						id={"template-picker-search"}
						onChange={(event) => setSearchText(event.target.value)}
						placeholder={"Search"}
						value={searchText}
					/>
				</div>
			</div>

			{content}

			{templateToDelete ? (
				<DeleteTemplateModal
					onClose={() => setTemplateToDelete(undefined)}
					onConfirm={handleConfirmDelete}
					templateName={templateToDelete.templateName}
				/>
			) : null}
		</div>
	);
};
